// MARK://dhk8/pos/features/DiscountDialog
// Apply a discount to the active order — rebuilt as a BottomSheet for the 534×738
// portrait POS. Porcentaje / Monto-fijo pill toggle, a full-width value input, a
// full-width "motivo" textarea, and a spaced ghost/green footer. When the order is
// split into sub-accounts, a target picker (Toda la cuenta / Cuenta A / …) is shown.
// All `--pos-*` tokens — theme-aware. Port of sc-restaurant DiscountDialog.tsx.

import { useEffect, useMemo, useState } from 'react'
import { useOrderStore, type DiscountKind, type DiscountTarget } from '../stores/order-store.js'
import { formatCents } from '../lib/money.js'
import BottomSheet from './pos/BottomSheet.js'
import { requestManagerPin } from './security/pin-gate-store.js'
import { discountNeedsGate } from '../lib/security-policy.js'

export default function DiscountDialog({ onClose }: { onClose: () => void }) {
  const order = useOrderStore((s) => s.order)
  const applyDiscount = useOrderStore((s) => s.applyDiscount)
  const subAccounts = order?.subAccounts ?? []
  const hasSubs = subAccounts.length > 1

  const [kind, setKind] = useState<DiscountKind>('PERCENT')
  const [value, setValue] = useState('')
  const [reason, setReason] = useState('')
  const [targetId, setTargetId] = useState<string | null>(null) // null = whole order
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { if (!hasSubs) setTargetId(null) }, [hasSubs])

  const baseCents = useMemo(() => {
    if (targetId) return subAccounts.find((s) => s.id === targetId)?.subtotalCents ?? 0
    return order?.subtotalCents ?? 0
  }, [targetId, subAccounts, order?.subtotalCents])

  const previewCents = useMemo(() => {
    const n = Number.parseFloat(value.replace(',', '.'))
    if (!Number.isFinite(n) || n <= 0) return 0
    return kind === 'PERCENT' ? Math.round((baseCents * Math.min(100, n)) / 100) : Math.round(n * 100)
  }, [value, kind, baseCents])

  async function handleApply() {
    const numericValue = Number.parseFloat(value.replace(',', '.'))
    if (!Number.isFinite(numericValue) || numericValue <= 0) { setError('Ingresa un valor válido'); return }
    if (kind === 'PERCENT' && numericValue > 100) { setError('El porcentaje no puede superar 100%'); return }
    // Discounts above policy (> 15 % or > $200) need a manager PIN to authorize.
    if (discountNeedsGate(kind, numericValue, baseCents)) {
      const { approved } = await requestManagerPin('order.bigDiscount')
      if (!approved) return
    }
    setBusy(true)
    setError(null)
    try {
      const target: DiscountTarget = targetId ? 'SUB_ACCOUNT' : 'ORDER'
      await applyDiscount({
        target,
        ...(targetId ? { targetId } : {}),
        kind,
        value: numericValue,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo aplicar el descuento')
    } finally {
      setBusy(false)
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => !busy && onClose()}
      title="Aplicar descuento"
      heightFraction={0.7}
      footer={
        <div className="dhk8-disc__foot">
          <button type="button" className="dhk8-disc__btn dhk8-disc__btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="button" className="dhk8-disc__btn dhk8-disc__btn--primary" onClick={() => void handleApply()} disabled={busy}>
            {busy ? 'Aplicando…' : previewCents > 0 ? `Aplicar · −${formatCents(previewCents)}` : 'Aplicar'}
          </button>
        </div>
      }
    >
      <div className="dhk8-disc">
        {hasSubs && (
          <div className="dhk8-disc__field">
            <span className="dhk8-disc__label">Aplicar a</span>
            <div className="dhk8-disc__targets">
              <button type="button" className={`dhk8-disc__chip${targetId === null ? ' dhk8-disc__chip--on' : ''}`} onClick={() => setTargetId(null)}>Toda la cuenta</button>
              {subAccounts.map((sa) => (
                <button key={sa.id} type="button" className={`dhk8-disc__chip${targetId === sa.id ? ' dhk8-disc__chip--on' : ''}`} onClick={() => setTargetId(sa.id)}>
                  {sa.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="dhk8-disc__field">
          <span className="dhk8-disc__label">Tipo</span>
          <div className="dhk8-disc__kinds">
            <button type="button" className={`dhk8-disc__chip${kind === 'PERCENT' ? ' dhk8-disc__chip--on' : ''}`} onClick={() => setKind('PERCENT')}>Porcentaje</button>
            <button type="button" className={`dhk8-disc__chip${kind === 'AMOUNT' ? ' dhk8-disc__chip--on' : ''}`} onClick={() => setKind('AMOUNT')}>Monto fijo</button>
          </div>
        </div>

        <div className="dhk8-disc__field">
          <span className="dhk8-disc__label">{kind === 'PERCENT' ? 'Porcentaje (%)' : 'Monto ($)'}</span>
          <input
            type="text"
            inputMode="decimal"
            className="dhk8-disc__input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={kind === 'PERCENT' ? '10' : '50.00'}
            autoFocus
          />
          <span className="dhk8-disc__hint">Base: {formatCents(baseCents)}{previewCents > 0 ? ` → descuento ${formatCents(previewCents)}` : ''}</span>
        </div>

        <div className="dhk8-disc__field">
          <span className="dhk8-disc__label">Motivo (opcional)</span>
          <textarea
            className="dhk8-disc__textarea"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Cortesía, promoción, ajuste de gerente…"
          />
        </div>

        {error && <div className="dhk8-disc__alert">{error}</div>}
      </div>
      <style>{styles}</style>
    </BottomSheet>
  )
}

const styles = `
  .dhk8-disc { display:flex; flex-direction:column; gap:var(--pos-space-5,20px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px) var(--pos-space-4,16px); }
  .dhk8-disc__field { display:flex; flex-direction:column; gap:var(--pos-space-2,8px); }
  .dhk8-disc__label { font-size:var(--pos-text-xxs,11px); letter-spacing:.08em; text-transform:uppercase; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-disc__hint { font-size:var(--pos-text-xxs,11px); color:var(--pos-fg-tertiary,#7E8C82); font-variant-numeric:tabular-nums; }
  .dhk8-disc__targets, .dhk8-disc__kinds { display:flex; flex-wrap:wrap; gap:var(--pos-space-2,8px); }
  .dhk8-disc__chip { min-height:var(--pos-touch-target,44px); padding:0 var(--pos-space-4,16px); display:inline-flex; align-items:center; justify-content:center; border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-pill,999px); background:transparent; color:var(--pos-fg,#E8EFE9); font:inherit; font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); cursor:pointer; transition:all var(--pos-duration-fast,120ms) ease; }
  .dhk8-disc__chip:hover { background:var(--pos-elevated,#1E2520); }
  .dhk8-disc__chip--on { background:var(--pos-tint,#1F8B3A); border-color:var(--pos-tint,#1F8B3A); color:#fff; box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-disc__input { width:100%; min-height:var(--pos-touch-target,44px); padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0D110F); color:var(--pos-fg,#E8EFE9); font:inherit; font-size:var(--pos-text-lg,17px); font-variant-numeric:tabular-nums; outline:none; transition:border-color var(--pos-duration-fast,120ms) ease, box-shadow var(--pos-duration-fast,120ms) ease; }
  .dhk8-disc__input:focus { border-color:var(--pos-tint,#1F8B3A); box-shadow:0 0 0 3px var(--pos-tint-soft,rgba(31,139,58,.12)); }
  .dhk8-disc__textarea { width:100%; min-height:64px; padding:var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0D110F); color:var(--pos-fg,#E8EFE9); font:inherit; font-size:var(--pos-text-md,15px); line-height:1.4; resize:vertical; outline:none; transition:border-color var(--pos-duration-fast,120ms) ease, box-shadow var(--pos-duration-fast,120ms) ease; }
  .dhk8-disc__textarea:focus { border-color:var(--pos-tint,#1F8B3A); box-shadow:0 0 0 3px var(--pos-tint-soft,rgba(31,139,58,.12)); }
  .dhk8-disc__textarea::placeholder { color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-disc__alert { color:var(--pos-danger,#E5484D); background:color-mix(in oklch, var(--pos-danger,#E5484D) 14%, transparent); border:1px solid color-mix(in oklch, var(--pos-danger,#E5484D) 40%, transparent); border-radius:var(--pos-radius-md,8px); padding:var(--pos-space-3,12px); font-size:var(--pos-text-sm,13px); }
  .dhk8-disc__foot { display:flex; gap:var(--pos-space-3,12px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px) var(--pos-space-4,16px); }
  .dhk8-disc__btn { flex:1; min-height:var(--pos-touch-target,44px); display:inline-flex; align-items:center; justify-content:center; border-radius:var(--pos-radius-md,8px); font:inherit; font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-medium,500); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-disc__btn:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-disc__btn--ghost { border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:transparent; color:var(--pos-fg,#E8EFE9); }
  .dhk8-disc__btn--ghost:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); }
  .dhk8-disc__btn--primary { border:1px solid var(--pos-tint,#1F8B3A); background:var(--pos-tint,#1F8B3A); color:#fff; font-weight:var(--pos-weight-semibold,600); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-disc__btn--primary:hover:not(:disabled) { filter:brightness(1.06); }
`
